/**
 * src/lib/photos/regionShapes.js — typed, renderer-agnostic annotation shapes.
 *
 * Phase 2 (2026-06-06): photo annotations grow from rectangle-only to a SHAPE
 * PALETTE — box, circle (ellipse), arrow, and pin — so the team can mark up a
 * photo for manufacturing ("circle this", "arrow points here", "pin this spot").
 *
 * As of Phase B the consumer is a TASK (`tasks.region` jsonb), not a photo_comment
 * — a mark IS a work item. The geometry is unchanged; only the region grows a
 * `type` discriminator. All coordinates are NORMALIZED 0..1
 * (a fraction of the image's natural size) so a mark drawn on a 4000px original
 * lands correctly when the lightbox renders it at 360px.
 *
 *   rect     { type:'rect',    x, y, w, h }            top-left + size
 *   ellipse  { type:'ellipse', cx, cy, rx, ry }        centre + radii (a circle = rx≈ry)
 *   arrow    { type:'arrow',   x1, y1, x2, y2 }         tail -> head
 *   pin      { type:'pin',     x, y }                   single point
 *   legacy   { x, y, w, h }  (no type)                  treated as 'rect' (back-compat)
 *
 * This module is the SINGLE place that knows the shape math; the SVG canvas and
 * the unit test pin the contract here (NAMING & FUNCTION REUSE rule). It is
 * deliberately free of any annotation-library / DOM dependency.
 */

export const SHAPE_TYPES = ['rect', 'ellipse', 'arrow', 'pin'];

function clamp01(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
function fin(n) { return typeof n === 'number' && Number.isFinite(n); }

/**
 * The shape type of a region. A legacy region ({x,y,w,h} with no `type`) is a
 * rectangle. Returns null when the value isn't a usable shape object.
 * @param {any} region
 * @returns {'rect'|'ellipse'|'arrow'|'pin'|null}
 */
export function regionType(region) {
  if (!region || typeof region !== 'object' || Array.isArray(region)) return null;
  if (typeof region.type === 'string') {
    return SHAPE_TYPES.includes(region.type) ? region.type : null;
  }
  // Legacy untyped rectangle.
  if (fin(region.x) && fin(region.y) && fin(region.w) && fin(region.h)) return 'rect';
  return null;
}

/**
 * Is this a usable, in-bounds shape? Guards the render + save paths so a
 * malformed/null region (a whole-photo comment) simply doesn't draw.
 * @param {any} region
 * @returns {boolean}
 */
export function isValidRegion(region) {
  const type = regionType(region);
  if (!type) return false;
  switch (type) {
    case 'rect':
      return [region.x, region.y, region.w, region.h].every(fin) && region.w > 0 && region.h > 0;
    case 'ellipse':
      return [region.cx, region.cy, region.rx, region.ry].every(fin) && region.rx > 0 && region.ry > 0;
    case 'arrow':
      return [region.x1, region.y1, region.x2, region.y2].every(fin)
        && (region.x1 !== region.x2 || region.y1 !== region.y2);
    case 'pin':
      return fin(region.x) && fin(region.y);
    default:
      return false;
  }
}

/**
 * Bounding box of a NORMALIZED region in 0..1 space, as { x, y, w, h }
 * (top-left + size). A pin is a zero-size point box. Returns null when the
 * region isn't a usable shape. This is the SINGLE bbox helper — the SVG canvas
 * (PhotoAnnotator) and the crop util (cropToRegion) both consume it so geometry
 * never forks (NAMING & FUNCTION REUSE rule).
 * @param {any} region — normalized region
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function regionBBox(region) {
  const type = regionType(region);
  if (!type) return null;
  switch (type) {
    case 'rect':
      return { x: region.x, y: region.y, w: region.w, h: region.h };
    case 'ellipse':
      return { x: region.cx - region.rx, y: region.cy - region.ry, w: 2 * region.rx, h: 2 * region.ry };
    case 'arrow':
      return {
        x: Math.min(region.x1, region.x2), y: Math.min(region.y1, region.y2),
        w: Math.abs(region.x2 - region.x1), h: Math.abs(region.y2 - region.y1),
      };
    case 'pin':
      return { x: region.x, y: region.y, w: 0, h: 0 };
    default:
      return null;
  }
}

/**
 * Normalized region -> PIXEL geometry for SVG rendering, given the rendered
 * image box {width,height} (px). Returns `{ type, ... }` mirroring the region
 * shape with pixel values, or null if the region/dims are unusable.
 * @param {object} region — normalized region
 * @param {{width:number,height:number}} dims — rendered image size (px)
 */
export function denormalize(region, dims) {
  const type = regionType(region);
  const W = fin(dims?.width) ? dims.width : 0;
  const H = fin(dims?.height) ? dims.height : 0;
  if (!type || W <= 0 || H <= 0 || !isValidRegion(region)) return null;
  switch (type) {
    case 'rect':
      return { type, x: clamp01(region.x) * W, y: clamp01(region.y) * H, w: clamp01(region.w) * W, h: clamp01(region.h) * H };
    case 'ellipse':
      return { type, cx: clamp01(region.cx) * W, cy: clamp01(region.cy) * H, rx: clamp01(region.rx) * W, ry: clamp01(region.ry) * H };
    case 'arrow':
      return { type, x1: clamp01(region.x1) * W, y1: clamp01(region.y1) * H, x2: clamp01(region.x2) * W, y2: clamp01(region.y2) * H };
    case 'pin':
      return { type, x: clamp01(region.x) * W, y: clamp01(region.y) * H };
    default:
      return null;
  }
}

/**
 * Build a normalized region from a draw gesture in PIXELS. `start`/`end` are
 * {x,y} pixel points relative to the rendered image box `dims` {width,height}.
 * A pin uses `start` only. Returns null if the gesture is degenerate (e.g. a
 * zero-size box) or dims are unusable — except a pin, which is always a point.
 *
 * @param {'rect'|'ellipse'|'arrow'|'pin'} type
 * @param {{x:number,y:number}} start — pointer-down point (px)
 * @param {{x:number,y:number}} [end] — pointer-up point (px); omitted for pin
 * @param {{width:number,height:number}} dims — rendered image size (px)
 * @returns {object|null} normalized region with a `type`
 */
export function buildRegion(type, start, end, dims) {
  const W = fin(dims?.width) ? dims.width : 0;
  const H = fin(dims?.height) ? dims.height : 0;
  if (W <= 0 || H <= 0 || !start || !fin(start.x) || !fin(start.y)) return null;
  const sx = clamp01(start.x / W), sy = clamp01(start.y / H);

  if (type === 'pin') return { type: 'pin', x: sx, y: sy };

  if (!end || !fin(end.x) || !fin(end.y)) return null;
  const ex = clamp01(end.x / W), ey = clamp01(end.y / H);

  switch (type) {
    case 'rect': {
      const region = { type: 'rect', x: Math.min(sx, ex), y: Math.min(sy, ey), w: Math.abs(ex - sx), h: Math.abs(ey - sy) };
      return isValidRegion(region) ? region : null;
    }
    case 'ellipse': {
      // Drag describes the bounding box; the ellipse is its inscribed oval.
      const cx = (sx + ex) / 2, cy = (sy + ey) / 2;
      const rx = Math.abs(ex - sx) / 2, ry = Math.abs(ey - sy) / 2;
      const region = { type: 'ellipse', cx, cy, rx, ry };
      return isValidRegion(region) ? region : null;
    }
    case 'arrow': {
      const region = { type: 'arrow', x1: sx, y1: sy, x2: ex, y2: ey };
      return isValidRegion(region) ? region : null;
    }
    default:
      return null;
  }
}

/**
 * The PAINTED box of an `object-fit: contain` image, inside its own element box.
 *
 * TICKET-818. Every mark on this canvas is stored normalised (0..1) against "the
 * rendered image", and both the pointer math and the SVG placement asked the `<img>`
 * for that box with `getBoundingClientRect()`. That is the ELEMENT box, and the two
 * are the same rectangle only when the element happens to match the picture's aspect
 * ratio. Give the element a width AND a height — which the annotate stage does — and
 * `contain` letterboxes the picture inside it, so the rect overstates the drawing
 * surface and every mark lands short of where the finger was. MEASURED in Chromium at
 * 393x852: element 882x460, painted 613x460, so a mark drawn at the right-hand edge of
 * the picture was recorded 134px inside it. A portrait photo is letterboxed at 1x, so
 * it was wrong before anyone zoomed.
 *
 * Returns offsets RELATIVE to the element box's top-left, so a caller adds them to the
 * element rect's origin. Falls back to the full element box when the natural size is
 * not known yet (an image that has not decoded reports 0x0) — the same answer the old
 * code gave, so a not-yet-loaded image degrades to today's behaviour rather than to
 * zero-sized geometry that would throw every mark into the corner.
 *
 * @param {{width:number,height:number}} element — the <img> element box (px)
 * @param {{naturalWidth:number,naturalHeight:number}} natural — the decoded bitmap size
 * @returns {{left:number,top:number,width:number,height:number}}
 */
export function containBox(element, natural) {
  const ew = fin(element?.width) ? element.width : 0;
  const eh = fin(element?.height) ? element.height : 0;
  const nw = fin(natural?.naturalWidth) ? natural.naturalWidth : 0;
  const nh = fin(natural?.naturalHeight) ? natural.naturalHeight : 0;
  if (ew <= 0 || eh <= 0 || nw <= 0 || nh <= 0) {
    return { left: 0, top: 0, width: Math.max(0, ew), height: Math.max(0, eh) };
  }
  const scale = Math.min(ew / nw, eh / nh);
  const width = nw * scale;
  const height = nh * scale;
  return { left: (ew - width) / 2, top: (eh - height) / 2, width, height };
}

/**
 * Normalize a PIXEL point to 0..1 image space using the box `dims` valid RIGHT NOW.
 * Call this the instant you read the point (pointerdown, each pointermove) rather
 * than stashing the raw pixel for later -- that's what makes the result immune to
 * the box resizing under a live gesture.
 *
 * TICKET-819a: the annotator's first mark landed off by ~6% of the image width
 * because the stage reflows mid-drag (the "no marks yet" hint disappears the
 * instant a draw preview exists), and pointerdown's raw pixel was later divided
 * by whatever box existed at pointerup -- two different boxes, one pixel value.
 * Normalizing at capture time means each end of the gesture is already expressed
 * in the same 0..1 space before a reflow can happen, so a later resize can't
 * retroactively change what an earlier point meant.
 *
 * @param {{x:number,y:number}} point — pixel point relative to the box's origin
 * @param {{width:number,height:number}} dims — the box valid at capture time (px)
 * @returns {{x:number,y:number}|null}
 */
export function normalizePoint(point, dims) {
  const W = fin(dims?.width) ? dims.width : 0;
  const H = fin(dims?.height) ? dims.height : 0;
  if (W <= 0 || H <= 0 || !point || !fin(point.x) || !fin(point.y)) return null;
  return { x: clamp01(point.x / W), y: clamp01(point.y / H) };
}

export const __test__ = { clamp01, fin };
